/**
 * Stage 4 on Sarvam: what a court reply is written from, and how far the
 * writing has got.
 *
 * Three surfaces, one object. Everything here is read off `drafting_usage.court`
 * — before drafting from the preflight, afterwards from the version the
 * workbench already polls — so the three cannot disagree about which papers
 * were sent:
 *
 * - **Before drafting** (`CourtBeforeDrafting`): the papers found, what is
 *   expected and not uploaded, and the size against the limit.
 *   What is missing is *named and never blocks* (scope item 2).
 * - **While drafting** (`CourtProgress`): the state in plain words, counted,
 *   with the page left usable. Never a spinner alone. A draft that stopped can
 *   be continued; the replies already written are kept.
 * - **Afterwards** (`PapersUsed`): one collapsed section with every file and
 *   what it is, which copy of the department's earlier reply was used, the
 *   pages left out and why, every line taken out of a page that was sent (with
 *   its words), and the Hindi passages relied on.
 *
 * **What a draft started again carried, and what continuing would keep, are the
 * server's sentences** (`carried`, `continue_says`). This screen used to assert
 * "Continue drafting makes a new version that carries them" — true only while
 * the papers and the paragraphs are unchanged, and silent when they were not.
 *
 * A version drafted the earlier way has no `court` object, and none of this
 * renders for it.
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { BlockView, CourtDraftView, CourtPaperView } from '../api/client'
import {
  FILED_COPY_WORDS,
  estimateWords,
  filedCopyUsed,
  leftOutWords,
  pagesWords,
  paperWords,
  readCourtProgress,
  sizeWords,
} from '../lib/court'
import { ErrorEnvelope } from './bits'

const number = (value: number | null | undefined) => (value ?? 0).toLocaleString('en-IN')

/**
 * The drafting service is not running.
 *
 * Said in plain words wherever a draft is waiting on it: that it is not
 * running, that nothing has been lost, and what can still be done. The same
 * outage described as the software being down is a different product to an
 * officer, so the sentence names the one thing that is unavailable and the
 * page beneath it stays usable — nothing is greyed because of it. The server's
 * own sentence is kept whole beneath ours.
 */
export function ServiceNotRunning({
  said,
  still,
}: {
  /** The server's sentence (`service_unavailable`). Nothing renders without it. */
  said: string | null | undefined
  /** What is kept and what can still be done here, in a sentence. */
  still: string
}) {
  if (!said) return null
  return (
    <div className="court-unavailable" role="status" data-testid="service-unavailable">
      <p>
        <strong>The drafting service is not running. Nothing has been lost.</strong>
      </p>
      <p className="small">{still}</p>
      <p className="small muted">{said}</p>
    </div>
  )
}

function PaperLine({ caseId, paper }: { caseId: string; paper: CourtPaperView }) {
  const copy = filedCopyUsed(paper)
  const unreadable = paper.unreadable_pages?.length ?? 0
  const facts = [
    pagesWords(paper.pages),
    paper.annexures?.length
      ? `cut into ${paper.annexures.length} ${paper.annexures.length === 1 ? 'annexure' : 'annexures'}`
      : '',
    unreadable ? `${unreadable} ${unreadable === 1 ? 'page' : 'pages'} could not be read` : '',
    paper.copies
      ? `${paper.copies} ${paper.copies === 1 ? 'page is a copy' : 'pages are copies'} of an earlier page`
      : '',
  ].filter(Boolean)
  return (
    <li>
      <span className="mono small">{paper.label}</span>
      <span>
        {paper.source_file_id ? (
          <a
            href={api.sourceFilePath(caseId, paper.source_file_id)}
            target="_blank"
            rel="noreferrer"
          >
            {paper.title}
          </a>
        ) : (
          <strong>{paper.title}</strong>
        )}
        {/* What the paper is and which pleading it belongs to, as the draft
            itself recorded them — not looked up on the papers list, which may
            have been marked differently since. */}
        <span className="muted"> — {paperWords(paper)}</span>
        {paper.answered ? (
          <span className="chip accent court-answered" data-testid="paper-answered">
            The paper being answered
          </span>
        ) : null}
        {facts.length ? <span className="small muted"> · {facts.join(' · ')}</span> : null}
        {copy ? <span className="court-copy small">{FILED_COPY_WORDS[copy]}</span> : null}
        {paper.settled_says ? (
          /* The upload did not say what this file is and the app named it by
             rule. The server's sentence: what it was taken as, why, and how to
             put it right. */
          <span className="court-copy small" data-testid="paper-settled">
            {paper.settled_says}
          </span>
        ) : null}
        {paper.awaiting_filed_copy ? (
          /* Exactly what is so: the filed copy IS uploaded, and is not read yet. */
          <span className="court-copy small" data-testid="awaiting-filed-copy">
            The copy as filed (<span className="mono">{paper.awaiting_filed_copy}</span>) has
            been uploaded and is still being read. Until it has been read, this is what the
            draft is written from.
          </span>
        ) : null}
        {paper.in_checks === false ? (
          <span className="court-copy small" data-testid="not-in-checks">
            Nobody signed or filed this draft, so the replies are not checked against it: a date
            or a number that is only here is reported as not found in the papers.
          </span>
        ) : null}
      </span>
    </li>
  )
}

function Absent({ absent }: { absent: string[] }) {
  if (!absent.length) return null
  return (
    <div className="court-absent" data-testid="court-absent">
      <strong>Expected and not uploaded</strong>
      <ul>
        {absent.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
      <p className="small muted">
        Drafting does not wait for these. The replies are written from the papers that are here.
      </p>
    </div>
  )
}

function NotSent({ court }: { court: CourtDraftView }) {
  const rows = court.not_sent ?? []
  if (!rows.length) return null
  return (
    <div>
      <strong>On the case, and not used for this document</strong>
      <ul>
        {rows.map((row) => (
          <li key={`${row.source_file_id ?? ''}${row.title}`}>
            <span className="mono small">{row.title}</span> — {row.reason}
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------------------------------------------------------- before drafting */

export function CourtBeforeDrafting({
  caseId,
  court,
}: {
  caseId: string
  court: CourtDraftView
}) {
  const papers = court.papers ?? []
  const unreadable = (court.left_out ?? []).filter((entry) => entry.what !== 'copy').length
  const size = sizeWords(court)
  const estimate = estimateWords(court)
  return (
    <section
      className="court-before"
      aria-label="What this draft will be written from"
      data-testid="court-before"
    >
      <h3>What this draft will be written from</h3>
      {/* Said before anything is started, and nothing is greyed by it: a draft
          asked for now is queued and written when the service is back. */}
      <ServiceNotRunning
        said={court.service_unavailable}
        still="A draft started now waits in the queue and is written when the service is running again. Papers can still be uploaded, and the versions already drafted can be read and downloaded."
      />
      <ul className="court-paper-list">
        {papers.map((paper) => (
          <PaperLine key={paper.label} caseId={caseId} paper={paper} />
        ))}
      </ul>
      <Absent absent={court.absent ?? []} />
      <NotSent court={court} />
      {unreadable ? (
        <p className="small">
          {unreadable} {unreadable === 1 ? 'page' : 'pages'} could not be read and{' '}
          {unreadable === 1 ? 'is' : 'are'} left out. They are listed with the draft, each with
          a link to the scan.
        </p>
      ) : null}
      <dl className="court-facts">
        {court.paragraphs_total ? (
          <>
            <dt>Paragraphs to answer</dt>
            <dd>{court.paragraphs_total}</dd>
          </>
        ) : estimate ? (
          /* Before the pleading has been cut into its paragraphs the count is
             a rough one, and it says so. */
          <>
            <dt>Paragraphs to answer</dt>
            <dd data-testid="court-estimate">{estimate}</dd>
          </>
        ) : null}
        <dt>Size</dt>
        <dd>
          {size ?? 'Not measured.'}{' '}
          <span className="small muted mono">
            about {number(court.block_tokens)} tokens of {number(court.block_token_limit)}
          </span>
        </dd>
      </dl>
      {/* Before drafting, two of the server's notes say again what this panel
          already shows or what only matters later: that the paragraph count is
          an estimate (said beside the count), and how a reply is revised after
          the draft (said on the draft). They stay on the draft itself. */}
      {(court.notes ?? [])
        .filter(
          (note) =>
            !note.startsWith('Revising a reply') &&
            !note.startsWith('The paragraphs of this pleading have not been marked yet'),
        )
        .map((note) => (
          <p key={note} className="small muted">
            {note}
          </p>
        ))}
    </section>
  )
}

/* ----------------------------------------------------------- while drafting */

const DETAIL: Partial<Record<CourtDraftView['state'], string>> = {
  queued: 'Nothing has been sent for drafting yet.',
  mapping:
    'The pleading is being cut into its numbered paragraphs. The replies are drafted after that.',
  drafting:
    'The replies are written about thirty paragraphs at a time and appear below as each group is finished.',
  repairing:
    'Every date, order number and quotation is being checked against the papers. A reply that does not pass is sent back once.',
}

/** Minutes and seconds, as a person says them: "40 seconds", "3 min 20 s". */
export function spanWords(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds))
  if (whole < 60) return `${whole} seconds`
  const minutes = Math.floor(whole / 60)
  const rest = whole % 60
  return rest ? `${minutes} min ${rest} s` : `${minutes} min`
}

/** A span as a rough figure: "about 6 minutes", "under a minute". */
export function aboutWords(seconds: number): string {
  if (seconds < 50) return 'under a minute'
  const minutes = Math.max(1, Math.round(seconds / 60))
  return `about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
}

/**
 * How long the group now being written has been running, and about how long
 * such a group — and the rest of the draft — takes.
 *
 * Thirty paragraphs go in one call and nothing lands until all thirty are
 * back: four to seven minutes in which the count above does not move. Without
 * this the screen reads as stuck. The start time and both estimates are the
 * server's; only the clock runs here.
 */
function BatchClock({ court }: { court: CourtDraftView }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(timer)
  }, [])
  if (court.state !== 'drafting' || !court.batch_started_at) return null
  const elapsed = (now - new Date(court.batch_started_at).getTime()) / 1000
  if (!Number.isFinite(elapsed) || elapsed < 0) return null
  const size = court.batch_paragraphs
  const expected = court.batch_expected_seconds
  const remaining = court.remaining_expected_seconds
  const left = remaining != null ? remaining - elapsed : null
  return (
    <p className="small" data-testid="court-batch-clock">
      This group{size ? ` of ${size} ${size === 1 ? 'paragraph' : 'paragraphs'}` : ''} has been
      running for <span className="mono">{spanWords(elapsed)}</span>.
      {expected
        ? ` A group this size takes ${aboutWords(expected)}; nothing appears until the whole group is back.`
        : ''}
      {left != null
        ? left > 45
          ? ` The whole draft should be back in ${aboutWords(left)}.`
          : ' The whole draft should be back shortly.'
        : ''}
      {expected && elapsed > expected * 2
        ? ' It is taking longer than usual; it is still running.'
        : ''}
    </p>
  )
}

export function CourtProgress({
  court,
  blocks,
  onContinue,
  onReadAgain,
  continuing = false,
  continueError,
  cannotContinue = '',
}: {
  court: CourtDraftView
  blocks: BlockView[]
  onContinue?: () => void
  /** Draft again with the pleading's paragraphs read afresh. Offered only on a
   *  draft that stopped before its first reply — there is nothing to lose. */
  onReadAgain?: () => void
  continuing?: boolean
  continueError?: unknown
  /** Why Continue cannot be offered, in words. Empty when it can. */
  cannotContinue?: string
}) {
  const reading = readCourtProgress(court, blocks)
  const findings = court.paragraphs_with_findings ?? 0
  return (
    <section
      className={`court-progress ${reading.tone}`}
      role="status"
      aria-label="Drafting progress"
      data-testid="court-progress"
    >
      <p className="court-progress-head">
        <strong>
          {reading.headline}
          {reading.live ? '…' : '.'}
        </strong>
        {reading.count ? <span className="mono small"> {reading.count}</span> : null}
      </p>
      {reading.unattended ? (
        /* Not a slow draft: nothing is writing it. The sentences that describe
           work in progress are left out, because none is. */
        <ServiceNotRunning
          said={court.service_unavailable}
          still="The replies already written are kept, and the drafting carries on from where it is when the service is running again. You can still read and download the replies below, and upload papers."
        />
      ) : null}
      {!reading.unattended && DETAIL[court.state] ? (
        <p className="small">{DETAIL[court.state]}</p>
      ) : null}
      {reading.live && !reading.unattended ? <BatchClock court={court} /> : null}
      {reading.live && !reading.unattended ? (
        <p className="small muted">
          You can leave this page and come back. The drafting carries on in the background, and
          the bell at the top tells you when the draft is back. A reply can be edited once the
          drafting has finished.
        </p>
      ) : null}
      {court.state === 'complete' && findings ? (
        <p className="small">
          {findings} {findings === 1 ? 'reply has' : 'replies have'} something to check before
          filing. Each is marked on its reply below.
        </p>
      ) : null}
      {court.carried ? <Carried court={court} /> : null}
      {court.state === 'stopped' ? (
        <>
          {/* What continuing keeps is the server's to say: it is true only
              while the papers and the paragraphs are unchanged, and the server
              says that. A version stopped before the server said it carries no
              sentence, and the screen then promises nothing of its own. */}
          <p className="small" data-testid="continue-says">
            {court.continue_says ??
              'Continue drafting makes a new version. That version says what it carried over from this one, and what it could not.'}
          </p>
          <div className={cannotContinue ? 'blocked inline unsettled' : 'row'}>
            <button
              type="button"
              className="btn"
              disabled={Boolean(cannotContinue) || continuing || !onContinue}
              onClick={onContinue}
              data-testid="court-continue"
            >
              {continuing ? 'Starting…' : 'Continue drafting'}
            </button>
            {onReadAgain && !cannotContinue && (court.paragraphs_written ?? 0) === 0 ? (
              /* A reading that missed a paragraph is not put right by going on
                 from it. No reply is written on this version, so reading again
                 costs the reading and loses nothing. */
              <button
                type="button"
                className="btn ghost"
                disabled={continuing}
                onClick={onReadAgain}
                data-testid="court-read-again"
              >
                Read the paragraphs again
              </button>
            ) : null}
            {cannotContinue ? <span className="why">{cannotContinue}</span> : null}
          </div>
          <ErrorEnvelope error={continueError} />
        </>
      ) : null}
      {(court.notes ?? [])
        .filter((note) => note !== court.carried?.says)
        .map((note) => (
          <p key={note} className="small muted">
            {note}
          </p>
        ))}
    </section>
  )
}

/**
 * On a draft started again: what it took from the version before it, in the
 * server's own sentence. Where the earlier replies (and the edits accepted on
 * them) were not carried, that is said in ink with the reason and the version
 * that still holds them — never left to be discovered from a page of replies
 * being drafted afresh.
 */
function Carried({ court }: { court: CourtDraftView }) {
  const carried = court.carried
  if (!carried?.says) return null
  return (
    <p
      className={`court-carried small ${carried.why_not ? '' : 'kept'}`}
      role="status"
      data-testid="court-carried"
    >
      {carried.why_not ? <strong>{carried.says}</strong> : carried.says}
    </p>
  )
}

/* ----------------------------------------------------------------- the cost */

/*
 * **No cost on any officer's screen** (decided 18 Oct 2026, at the release that
 * took this build over). The per-call table and the rupee line that used to sit
 * here, inside *Papers this draft was written from*, are gone, and so is the
 * estimate shown before drafting. Every call is still recorded with its tokens
 * and its estimated rupees on the version (`drafting_usage`), for the team to
 * read from the record — `api/tools/drafting_cost_report.py` — and never for
 * the officer to act on.
 */

/* ------------------------------------------------- what it was written from */

export function PapersUsed({ caseId, court }: { caseId: string; court: CourtDraftView }) {
  const papers = court.papers ?? []
  const byLabel = new Map(papers.map((paper) => [paper.label, paper]))
  const leftOut = court.left_out ?? []
  const lines = court.lines_left_out ?? []
  const linesTotal = court.lines_left_out_total ?? lines.length
  const translated = court.translated ?? []
  return (
    <details className="d18-papers-used" data-testid="papers-used">
      <summary>
        Papers this draft was written from{' '}
        <span className="small muted">
          {papers.length} {papers.length === 1 ? 'file' : 'files'}
          {leftOut.length
            ? ` · ${leftOut.length} ${leftOut.length === 1 ? 'page' : 'pages'} left out`
            : ''}
          {linesTotal
            ? ` · ${linesTotal} ${linesTotal === 1 ? 'line' : 'lines'} taken out of pages`
            : ''}
          {translated.length
            ? ` · ${translated.length} Hindi ${translated.length === 1 ? 'passage' : 'passages'} to confirm`
            : ''}
        </span>
      </summary>
      <div className="d18-papers-body">
        {court.origin_version ? (
          /* The version open here was made by an accepted edit. The papers,
             and the findings came with it from the version the
             drafting itself wrote, and that is said — quietly, once. */
          <p className="small muted" data-testid="origin-version">
            First written as version {court.origin_version}. The papers below are that draft’s;
            a reply edited since has been checked again.
          </p>
        ) : null}
        <ul className="court-paper-list">
          {papers.map((paper) => (
            <PaperLine key={paper.label} caseId={caseId} paper={paper} />
          ))}
        </ul>
        <Absent absent={court.absent ?? []} />
        <NotSent court={court} />

        <h4>Pages left out, and why</h4>
        {leftOut.length ? (
          <ul>
            {leftOut.map((entry) => {
              const paper = byLabel.get(entry.paper)
              const words = leftOutWords(entry)
              return (
                <li key={`${entry.paper}-${entry.page_no}-${entry.what}`}>
                  <span className="mono small">{paper?.title ?? entry.paper}</span> ·{' '}
                  {words.lead}. <span className="muted">{entry.reason}</span>{' '}
                  {words.unreadable && paper?.source_file_id ? (
                    <a
                      href={`${api.sourceFilePath(caseId, paper.source_file_id)}#page=${entry.page_no}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open page {entry.page_no} of the scan
                    </a>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="small muted">No page was left out.</p>
        )}

        {/* Every line that was taken out of a page before it was sent, with
            its words, so that what was not sent can be read and not only
            counted. Collapsed: it is long, and it is for the officer who went
            looking. */}
        <h4>Lines taken out of pages that were sent</h4>
        {linesTotal ? (
          <details className="court-lines-out" data-testid="lines-left-out">
            <summary>
              {linesTotal} {linesTotal === 1 ? 'line' : 'lines'} — a picture the reader
              described, a rubber stamp’s words, the title printed on every cover sheet, or (in
              a pleading) a page’s printed number and the signature at its foot
            </summary>
            <p className="small muted">
              The page itself was sent; only these lines were not. The stored reading of the
              page is unchanged. If a line here is the paper’s own words, tell the office that
              maintains the app, and read the reply against the scan.
            </p>
            {linesTotal > lines.length ? (
              <p className="small" data-testid="lines-left-out-cut">
                The first {lines.length} of {linesTotal} are listed.
              </p>
            ) : null}
            <ul>
              {lines.map((entry, index) => {
                const paper = byLabel.get(entry.paper)
                return (
                  <li key={`${entry.paper}-${entry.page_no}-${index}`}>
                    <span className="mono small">{paper?.title ?? entry.paper}</span> · page{' '}
                    {entry.page_no} · <span className="muted">{entry.reason}</span>{' '}
                    <span className="words mono small">{entry.text}</span>
                  </li>
                )
              })}
            </ul>
          </details>
        ) : (
          <p className="small muted">No line was taken out of a page.</p>
        )}

        <h4>Hindi passages relied on — confirm against the originals</h4>
        {translated.length ? (
          <>
            <p className="small muted">
              The replies are in English and these passages of the record are in Hindi. The app
              rendered them; read each against the original before the reply is filed.
            </p>
            <ul>
              {translated.map((passage, index) => {
                const paper = byLabel.get(passage.paper)
                return (
                  <li
                    key={`${passage.paragraph}-${passage.paper}-${passage.page ?? ''}-${index}`}
                  >
                    Reply to paragraph <span className="mono">{passage.paragraph}</span> ·{' '}
                    {paper?.source_file_id ? (
                      <a
                        href={`${api.sourceFilePath(caseId, paper.source_file_id)}${passage.page ? `#page=${passage.page}` : ''}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {paper.title}
                        {passage.page ? `, page ${passage.page}` : ''}
                      </a>
                    ) : (
                      <span className="mono small">
                        {paper?.title ?? passage.paper}
                        {passage.page ? `, page ${passage.page}` : ''}
                      </span>
                    )}
                    {passage.about ? ` — ${passage.about}` : ''}
                  </li>
                )
              })}
            </ul>
          </>
        ) : (
          <p className="small muted">None was listed for this draft.</p>
        )}

        {court.carried ? (
          <>
            <h4>Carried over from an earlier version</h4>
            <Carried court={court} />
          </>
        ) : null}

        {(court.notes ?? []).filter((note) => note !== court.carried?.says).length ? (
          <>
            <h4>Notes on this draft</h4>
            {(court.notes ?? [])
              .filter((note) => note !== court.carried?.says)
              .map((note) => (
                <p key={note} className="small muted">
                  {note}
                </p>
              ))}
          </>
        ) : null}
      </div>
    </details>
  )
}
